import { Component, Element, Host, h, Prop, Event, EventEmitter } from '@stencil/core';
import { keyboardClickProps } from '../../utils/keyboard';
import { getRandomDOMId } from '../../utils/random';

export interface AhaImageSelectorItem extends HTMLAhaImageSelectorItemElement {}

/**
 * The `aha-image-selector-item` component is meant to be used inside `aha-image-selector`.
 * @slot label - contents of the `aha-image-selector-item` label.
 */
@Component({
  tag: 'aha-image-selector-item',
  styleUrl: 'aha-image-selector-item.css',
  shadow: true,
})
export class AhaImageSelectorItem {
  @Element() host!: HTMLAhaImageSelectorItemElement;

  /**
   * URL that specifies the path to the image.
   */
  @Prop({ reflect: true }) src!: string;

  /**
   * Alt text of the image.
   */
  @Prop({ reflect: true, attribute: 'alt' }) imageAlt!: string;

  /**
   * Whether this is the currently selected `aha-image-selector-item`.
   */
  @Prop({ reflect: true }) selected?: boolean = false;

  /**
   * Triggers after an aha-image-selector-item was selected.
   */
  @Event({ eventName: 'aha-image-selector-item:selected', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  wasSelected!: EventEmitter<HTMLAhaImageSelectorItemElement>;

  /**
   * Trigger after the component is connected
   */
  @Event({ eventName: 'aha-image-selector-item:connected', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  connected!: EventEmitter<HTMLAhaImageSelectorItemElement>;

  /**
   * Trigger after the component is disconnected
   */
  @Event({ eventName: 'aha-image-selector-item:disconnected', cancelable: true, bubbles: true }) //eslint-disable-line @stencil/decorators-style
  disconnected!: EventEmitter<HTMLAhaImageSelectorItemElement>;

  private handleClick = () => {
    this.host.focus();
    this.wasSelected.emit(this.host);
  };

  connectedCallback() {
    this.host.dataset.itemId = getRandomDOMId('aha-image-selector-item');
    this.connected.emit(this.host);
  }

  disconnectedCallback() {
    this.disconnected.emit(this.host);
  }

  render() {
    return (
      <Host
        aria-current={this.selected ? 'true' : 'false'}
        role="button"
        tabIndex={0}
        onClick={this.handleClick}
        {...keyboardClickProps()}
      >
        <div id="color-container">
          <div id="image-container">
            <img src={this.src} alt={this.imageAlt} />
          </div>
          <slot name="label"></slot>
        </div>
      </Host>
    );
  }
}
